import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment-timezone';
import _ from 'lodash';

import env from 'utils/env';
import PageContainer from 'shared-components/PageContainer/PageContainer';
import Button from 'shared-components/Button/Button';
import styles from './Banner.module.css';
import SharedProduct from './SharedProduct/SharedProduct';

const Banner = ({
  senderName,
  expirationDate,
  inviteState,
  product,
  viewSize,
  buttonText,
  buttonAction,
}) => {
  const formattedExpiration = moment
    .utc(expirationDate)
    .tz('America/Los_Angeles')
    .format('MM/DD/YYYY');

  const nameOrAfriend = senderName
    ? _.startCase(_.toLower(senderName))
    : 'a friend';
  const firstNameOrShe = senderName
    ? _.startCase(_.toLower(senderName.split(' ')[0]))
    : 'She';

  const productName = _.get(product, 'productName');

  const BANNER_COPY = {
    unredeemed: {
      textAboveTitle: `Psst, ${nameOrAfriend} is giving you insider access!`,
      title: 'You’re Invited to Our Exclusive Sale',
      subtitle: productName
        ? `${firstNameOrShe} thinks you would like the ${productName} in our sale. Join to shop premium products at up to 70% off.`
        : `${firstNameOrShe} is a pretty amazing friend. Join to shop premium products at up to 70% off.`,
      subtitle2: (
        <span>
          Hurry! Your invite expires on
          <strong>{` ${formattedExpiration} at 11:59 p.m. PT.`}</strong>
        </span>
      ),
      textBelowButton: 'Invite only. Free to join. No purchase necessary.',
      className: styles.unredeemed,
    },
    member: {
      textAboveTitle: `Psst, ${nameOrAfriend} is giving you insider access!`,
      title: "You're Invited to Our Exclusive Sale",
      subtitle: `${firstNameOrShe} is a pretty amazing friend. Join to shop premium products at up to 70% off.`,
      subtitle2: (
        <span>
          Hurry! Your invite expires on
          <strong>{` ${formattedExpiration} at 11:59 p.m. PT.`}</strong>
        </span>
      ),
      textBelowButton: 'Invite only. Free to join. No purchase necessary.',
      className: styles.unredeemed,
    },
    invalid: {
      title: 'This Invite is Not Valid',
      subtitle: 'Please log in to your account to shop and send invites.',
      className: styles.invalid,
    },
    expired: {
      title: 'Oops! Your Invite Has Expired',
      subtitle: 'Want to experience everything FabFitFun has to offer?',
      textBelowButton: (
        <span>
          {'Already have an account? '}
          <strong>
            <a href={`https://login.${env.hostname}`}>Log In</a>
          </strong>
        </span>
      ),
      className: styles.expired,
    },
  };

  const copy = BANNER_COPY[inviteState];
  if (!copy) return null;
  const {
    textAboveTitle,
    title,
    subtitle,
    subtitle2,
    textBelowButton,
    className,
  } = copy;

  return (
    <div
      className={classNames(
        styles.banner,
        className,
        productName ? styles.productMetadata : styles.hero
      )}
    >
      <PageContainer>
        {product && <SharedProduct product={product} viewSize={viewSize} />}
        <div className={styles.text}>
          {textAboveTitle && (
            <span className={styles.textAboveTitle}>{textAboveTitle}</span>
          )}
          <h2>{title}</h2>
          <p>{subtitle}</p>
          {subtitle2 && <p>{subtitle2}</p>}
          <Button onClick={buttonAction}>{buttonText}</Button>
          {textBelowButton && (
            <span className={styles.textBelowButton}>{textBelowButton}</span>
          )}
        </div>
      </PageContainer>
    </div>
  );
};

Banner.propTypes = {
  senderName: PropTypes.string,
  expirationDate: PropTypes.string,
  inviteState: PropTypes.string.isRequired,
  product: PropTypes.shape({}),
  viewSize: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  buttonAction: PropTypes.func.isRequired,
};

Banner.defaultProps = {
  senderName: '',
  expirationDate: '',
  product: null,
};

export default Banner;
